import {Layout} from '../../src/Layout';
export default Layout;

import docs from 'docs:react-aria-components';
import '../../tailwind/tailwind.css';
import {InlineAlert, Heading, Content} from '@react-spectrum/s2';
import Anatomy from './NavigationTreeAnatomy.svg';

export const tags = ['navigation', 'nav', 'sidebar'];
export const version = 'alpha';
export const description = 'A navigation component that displays a nested, hierarchical set of links, with support for keyboard navigation and a current route indicator.';

# NavigationTree

<PageDescription>{docs.exports.NavigationTree.description}</PageDescription>

<ExampleSwitcher>
  ```tsx render docs={docs.exports.NavigationTree} links={docs.links} props={[]} type="vanilla" files={["starters/docs/src/NavigationTree.tsx", "starters/docs/src/NavigationTree.css", "packages/dev/s2-docs/pages/react-aria/Router.tsx"]}
  "use client";
  import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent, NavigationTreeItemLink} from 'vanilla-starter/NavigationTree';
  import {Button} from 'vanilla-starter/Button';
  import {MoreHorizontal} from 'lucide-react';
  import {Router, Link as RouterLink} from './Router';

  <Router defaultSelectedRoute="/photos">
    {({selectedRoute}) => (
      <NavigationTree aria-label="Files" selectedRoute={selectedRoute} renderLink={props => <RouterLink {...props} />} defaultExpandedKeys={['files']}>
        <NavigationTreeItem id="home" href="/home" textValue="Home">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Home</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
        <NavigationTreeItem id="files" href="/files" textValue="Files">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Files</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
          <NavigationTreeItem id="photos" href="/photos" textValue="Photos">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Photos</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
          <NavigationTreeItem id="videos" href="/videos" textValue="Videos">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Videos</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
        </NavigationTreeItem>
        <NavigationTreeItem id="shared" textValue="Shared">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Shared</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
          <NavigationTreeItem id="food" href="/food" textValue="Food">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Food</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
          <NavigationTreeItem id="drinks" href="/drinks" textValue="Drinks">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Drinks</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
        </NavigationTreeItem>
      </NavigationTree>
    )}
  </Router>
  ```

  ```tsx render docs={docs.exports.NavigationTree} links={docs.links} props={[]} type="tailwind" files={["starters/tailwind/src/NavigationTree.tsx", "packages/dev/s2-docs/pages/react-aria/Router.tsx"]}
  "use client";
  import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent, NavigationTreeItemLink} from 'tailwind-starter/NavigationTree';
  import {Button} from 'tailwind-starter/Button';
  import {MoreHorizontal} from 'lucide-react';
  import {Router, Link as RouterLink} from './Router';

  <Router defaultSelectedRoute="/photos">
    {({selectedRoute}) => (
      <NavigationTree aria-label="Files" selectedRoute={selectedRoute} renderLink={props => <RouterLink {...props} />} defaultExpandedKeys={['files']}>
        <NavigationTreeItem id="home" href="/home" textValue="Home">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Home</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
        <NavigationTreeItem id="files" href="/files" textValue="Files">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Files</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
          <NavigationTreeItem id="photos" href="/photos" textValue="Photos">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Photos</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
          <NavigationTreeItem id="videos" href="/videos" textValue="Videos">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Videos</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
        </NavigationTreeItem>
        <NavigationTreeItem id="shared" textValue="Shared">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Shared</NavigationTreeItemLink>
            <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
          </NavigationTreeItemContent>
          <NavigationTreeItem id="food" href="/food" textValue="Food">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Food</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
          <NavigationTreeItem id="drinks" href="/drinks" textValue="Drinks">
            <NavigationTreeItemContent>
              <NavigationTreeItemLink>Drinks</NavigationTreeItemLink>
              <Button variant="quiet" aria-label="More options"><MoreHorizontal size={16} aria-hidden /></Button>
            </NavigationTreeItemContent>
          </NavigationTreeItem>
        </NavigationTreeItem>
      </NavigationTree>
    )}
  </Router>
  ```

</ExampleSwitcher>

## Content

`NavigationTree` follows the [Collection Components API](collections?component=NavigationTree), accepting both static and dynamic collections. The example above shows a static collection. This example shows a dynamic collection, passing a list of objects to the `items` prop and a function to render the children.

```tsx render files={["packages/dev/s2-docs/pages/react-aria/Router.tsx"]}
"use client";
import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent, NavigationTreeItemLink} from 'vanilla-starter/NavigationTree';
import {Router, Link as RouterLink} from './Router';

function Example() {
  let items = [
    {id: 'overview', url: '/overview', label: 'Overview'},
    {id: 'reports', url: '/reports', label: 'Reports'},
    {id: 'settings', url: '/settings', label: 'Settings'}
  ];

  return (
    <Router defaultSelectedRoute="/reports">
      {({selectedRoute}) => (
        /*- begin highlight -*/
        <NavigationTree aria-label="Sections" items={items} selectedRoute={selectedRoute} renderLink={props => <RouterLink {...props} />}>
          {item => (
            <NavigationTreeItem href={item.url} textValue={item.label}>
              <NavigationTreeItemContent>
                <NavigationTreeItemLink>{item.label}</NavigationTreeItemLink>
              </NavigationTreeItemContent>
            </NavigationTreeItem>
          )}
        </NavigationTree>
        /*- end highlight -*/
      )}
    </Router>
  );
}
```

<InlineAlert variant="notice">
  <Heading>Accessibility</Heading>
  <Content>`NavigationTree` renders as a tree so keyboard users can navigate and expand the hierarchy. When it acts as the main navigation for a page, place it inside a [navigation landmark](https://www.w3.org/WAI/ARIA/apg/patterns/landmarks/examples/navigation.html): wrap the `NavigationTree` in a `<nav>` element with an `aria-label` so assistive technology users can quickly find it.</Content>
</InlineAlert>

### Sections

Use `NavigationTreeSection` to group related items, with an optional `NavigationTreeHeader` to label each group. Sections without a header must have an `aria-label`.

```tsx render files={["packages/dev/s2-docs/pages/react-aria/Router.tsx"]}
"use client";
import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent, NavigationTreeItemLink, NavigationTreeSection, NavigationTreeHeader} from 'vanilla-starter/NavigationTree';
import {Router, Link as RouterLink} from './Router';

<Router defaultSelectedRoute="/projects/apollo">
  {({selectedRoute}) => (
    <NavigationTree aria-label="Workspace" selectedRoute={selectedRoute} renderLink={props => <RouterLink {...props} />}>
      {/*- begin highlight -*/}
      <NavigationTreeSection>
        <NavigationTreeHeader>Personal</NavigationTreeHeader>
        <NavigationTreeItem href="/home" textValue="Home">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Home</NavigationTreeItemLink>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
        <NavigationTreeItem href="/starred" textValue="Starred">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Starred</NavigationTreeItemLink>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
      </NavigationTreeSection>
      {/*- end highlight -*/}
      <NavigationTreeSection>
        <NavigationTreeHeader>Projects</NavigationTreeHeader>
        <NavigationTreeItem href="/projects/apollo" textValue="Apollo">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Apollo</NavigationTreeItemLink>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
        <NavigationTreeItem href="/projects/gemini" textValue="Gemini">
          <NavigationTreeItemContent>
            <NavigationTreeItemLink>Gemini</NavigationTreeItemLink>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
      </NavigationTreeSection>
    </NavigationTree>
  )}
</Router>
```

## Current route

Each `NavigationTreeItem` accepts an `href`. Set the `selectedRoute` prop on the `NavigationTree` to the current page's path, and the item whose `href` matches is marked with `aria-current="page"` (and a `data-current` attribute for styling).

```tsx render files={["packages/dev/s2-docs/pages/react-aria/Router.tsx"]}
"use client";
import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent, NavigationTreeItemLink} from 'vanilla-starter/NavigationTree';
import {Router, Link as RouterLink} from './Router';

<Router defaultSelectedRoute="/inbox">
  {({selectedRoute}) => (
    /*- begin highlight -*/
    <NavigationTree aria-label="Mail" selectedRoute={selectedRoute} renderLink={props => <RouterLink {...props} />}>
      {/*- end highlight -*/}
      <NavigationTreeItem href="/inbox" textValue="Inbox">
        <NavigationTreeItemContent>
          <NavigationTreeItemLink>Inbox</NavigationTreeItemLink>
        </NavigationTreeItemContent>
      </NavigationTreeItem>
      <NavigationTreeItem href="/drafts" textValue="Drafts">
        <NavigationTreeItemContent>
          <NavigationTreeItemLink>Drafts</NavigationTreeItemLink>
        </NavigationTreeItemContent>
      </NavigationTreeItem>
      <NavigationTreeItem href="/sent" textValue="Sent">
        <NavigationTreeItemContent>
          <NavigationTreeItemLink>Sent</NavigationTreeItemLink>
        </NavigationTreeItemContent>
      </NavigationTreeItem>
    </NavigationTree>
  )}
</Router>
```

To integrate with a client side router, use the `render` prop on `Link` to delegate to your router's link component. This supports features like prefetching on hover and avoids full page reloads.
Get the current route from your framework and pass it as `selectedRoute`.

<ExampleSwitcher type="framework" examples={['Next.js', 'React Router']}>
  ```tsx
  import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent} from './NavigationTree';
  import {Link} from 'react-aria-components/Link';
  import NextLink from 'next/link';
  import {usePathname} from 'next/navigation';

  function Sidebar() {
    let selectedRoute = usePathname();
    return (
      <NavigationTree selectedRoute={selectedRoute}>
        <NavigationTreeItem href="/inbox" textValue="Inbox">
          <NavigationTreeItemContent>
            <Link render={props => <NextLink {...props} />}>Inbox</Link>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
      </NavigationTree>
    );
  }
  ```

  ```tsx
  import {NavigationTree, NavigationTreeItem, NavigationTreeItemContent} from './NavigationTree';
  import {Link} from 'react-aria-components/Link';
  import {Link as RouterLink, useLocation} from 'react-router';

  function Sidebar() {
    let {pathname} = useLocation();
    return (
      <NavigationTree selectedRoute={pathname}>
        <NavigationTreeItem href="/inbox" textValue="Inbox">
          <NavigationTreeItemContent>
            <Link render={props => <RouterLink {...props} to={props.href} />}>Inbox</Link>
          </NavigationTreeItemContent>
        </NavigationTreeItem>
      </NavigationTree>
    );
  }
  ```
</ExampleSwitcher>

## API

<Anatomy role="img" aria-label="Anatomy diagram of a NavigationTree, consisting of sections of items. Each item contains a link, and items with children include a chevron button to collapse and expand them." />

```tsx links={{NavigationTree: '#NavigationTree', NavigationTreeItem: '#NavigationTreeitem', NavigationTreeItemContent: '#NavigationTreeitemcontent', NavigationTreeSection: '#NavigationTreesection', NavigationTreeHeader: '#NavigationTreeheader', Link: 'Link'}}
<NavigationTree>
  <NavigationTreeSection>
    <NavigationTreeHeader />
    <NavigationTreeItem>
      <NavigationTreeItemContent>
        <Link />
        <Button slot="chevron" />
      </NavigationTreeItemContent>
    </NavigationTreeItem>
  </NavigationTreeSection>
</NavigationTree>
```

### NavigationTree

<PropTable component={docs.exports.NavigationTree} links={docs.links} showDescription />

### NavigationTreeItem

<PropTable component={docs.exports.NavigationTreeItem} links={docs.links} showDescription />

### NavigationTreeItemContent

<PropTable component={docs.exports.NavigationTreeItemContent} links={docs.links} showDescription hideRenderProps />

### NavigationTreeSection

<PropTable component={docs.exports.NavigationTreeSection} links={docs.links} showDescription />

### NavigationTreeHeader

<PropTable component={docs.exports.NavigationTreeHeader} links={docs.links} showDescription />
